import axios from "axios";
import { DateTime } from "luxon";
import { makeAutoObservable } from "mobx";
import {
  CatalogActivityLog,
  CatalogCategory,
  CatalogCategoryInput,
  CatalogKbLink,
  CatalogKbLinkInput,
  CatalogPaginationMeta,
  CatalogProduct,
  CatalogProductAlias,
  CatalogProductInput,
  CatalogResolveDocument,
} from "../models/CatalogV2";
import auth from "./auth";
import bots from "./bots";
import workspaces from "./workspaces";
import firebase from "../services/firebase";

type CatalogListFilters = {
  category?: string;
  status?: string;
  q?: string;
};

export type KbSource = { id: string; label: string };

export class CatalogV2Store {
  categories: CatalogCategory[] = [];
  products: CatalogProduct[] = [];
  kbLinks: CatalogKbLink[] = [];
  activityLogs: CatalogActivityLog[] = [];
  activityLogPagination: CatalogPaginationMeta = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  };
  kbSources: { faq: KbSource[]; article: KbSource[] } = { faq: [], article: [] };
  loading = false;
  saving = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Load the workspace's FAQs or articles so the Links picker offers real
   * sources (question / title) instead of asking the user to paste an id.
   */
  loadKbSources = async (type: "faq" | "article"): Promise<KbSource[]> => {
    const ws = workspaces.selectedWorkspace;
    if (!ws?.id) {
      return [];
    }

    const snap = await firebase
      .firestore()
      .collection("workspaces")
      .doc(ws.id)
      .collection(type === "faq" ? "faqs" : "articles")
      .orderBy("createdAt", "desc")
      .get();

    const items: KbSource[] = snap.docs.map((doc) => {
      const data = doc.data() as { question?: string; title?: string };
      return { id: doc.id, label: data.question || data.title || doc.id };
    });
    this.kbSources[type] = items;
    return items;
  };

  loadCategories = async () => {
    const bot = this.requireBot();
    this.loading = true;

    try {
      const { data } = await axios.get(`${this.baseUrl(bot.id)}/categories`, {
        params: this.authParams(bot),
      });
      this.categories = (data.data || []).map(this.categoryAdapter);
    } finally {
      this.loading = false;
    }
  };

  loadProducts = async (filters: CatalogListFilters = {}) => {
    const bot = this.requireBot();
    this.loading = true;

    try {
      const { data } = await axios.get(`${this.baseUrl(bot.id)}/products`, {
        params: {
          ...this.authParams(bot),
          ...filters,
        },
      });
      this.products = (data.data || []).map(this.productAdapter);
    } finally {
      this.loading = false;
    }
  };

  loadActivityLogs = async (page = 1, perPage = 10) => {
    const bot = this.requireBot();
    this.loading = true;

    try {
      const { data } = await axios.get(`${this.baseUrl(bot.id)}/logs`, {
        params: {
          ...this.authParams(bot),
          page,
          per_page: perPage,
        },
      });
      this.activityLogs = (data.data || []).map(this.activityLogAdapter);
      this.activityLogPagination = {
        currentPage: Number(data.meta?.current_page || page),
        perPage: Number(data.meta?.per_page || perPage),
        total: Number(data.meta?.total || 0),
        lastPage: Number(data.meta?.last_page || 1),
      };
    } finally {
      this.loading = false;
    }
  };

  createCategory = async (category: CatalogCategoryInput) => {
    const bot = this.requireBot();
    this.saving = true;

    try {
      const { data } = await axios.post(
        `${this.baseUrl(bot.id)}/categories`,
        this.categoryPayload(category),
        { params: this.authParams(bot) }
      );
      await this.loadCategories();
      return this.categoryAdapter(data.data);
    } finally {
      this.saving = false;
    }
  };

  updateCategory = async (id: number, category: Partial<CatalogCategoryInput>) => {
    const bot = this.requireBot();
    this.saving = true;

    try {
      const { data } = await axios.put(
        `${this.baseUrl(bot.id)}/categories/${id}`,
        this.categoryPayload(category),
        { params: this.authParams(bot) }
      );
      await this.loadCategories();
      return this.categoryAdapter(data.data);
    } finally {
      this.saving = false;
    }
  };

  deleteCategory = async (id: number) => {
    const bot = this.requireBot();
    await axios.delete(`${this.baseUrl(bot.id)}/categories/${id}`, {
      params: this.authParams(bot),
    });
    await this.loadCategories();
  };

  createProduct = async (product: CatalogProductInput) => {
    const bot = this.requireBot();
    this.saving = true;

    try {
      const { data } = await axios.post(
        `${this.baseUrl(bot.id)}/products`,
        this.productPayload(product),
        { params: this.authParams(bot) }
      );
      const created = this.productAdapter(data.data);
      this.products = [created, ...this.products.filter((item) => item.id !== created.id)];
      return created;
    } finally {
      this.saving = false;
    }
  };

  updateProduct = async (id: number, product: CatalogProductInput) => {
    const bot = this.requireBot();
    this.saving = true;

    try {
      const { data } = await axios.put(
        `${this.baseUrl(bot.id)}/products/${id}`,
        this.productPayload(product),
        { params: this.authParams(bot) }
      );
      const updated = this.productAdapter(data.data);
      this.products = this.products.map((item) => (item.id === id ? updated : item));
      return updated;
    } finally {
      this.saving = false;
    }
  };

  deleteProduct = async (id: number) => {
    const bot = this.requireBot();
    await axios.delete(`${this.baseUrl(bot.id)}/products/${id}`, {
      params: this.authParams(bot),
    });
    this.products = this.products.filter((product) => product.id !== id);
  };

  addAlias = async (productId: number, alias: string) => {
    const bot = this.requireBot();
    const { data } = await axios.post(
      `${this.baseUrl(bot.id)}/products/${productId}/aliases`,
      { alias },
      { params: this.authParams(bot) }
    );
    const created = this.aliasAdapter(data.data);
    this.products = this.products.map((product) =>
      product.id === productId
        ? {
            ...product,
            aliases: [...product.aliases, created.alias],
            aliasItems: [...(product.aliasItems || []), created],
          }
        : product
    );
    return created;
  };

  deleteAlias = async (productId: number, aliasId: number) => {
    const bot = this.requireBot();
    await axios.delete(
      `${this.baseUrl(bot.id)}/products/${productId}/aliases/${aliasId}`,
      { params: this.authParams(bot) }
    );
    this.products = this.products.map((product) =>
      product.id === productId
        ? {
            ...product,
            aliases: (product.aliasItems || [])
              .filter((alias) => alias.id !== aliasId)
              .map((alias) => alias.alias),
            aliasItems: (product.aliasItems || []).filter((alias) => alias.id !== aliasId),
          }
        : product
    );
  };

  loadKbLinks = async (productId?: number) => {
    const bot = this.requireBot();
    const { data } = await axios.get(`${this.baseUrl(bot.id)}/kb-links`, {
      params: {
        ...this.authParams(bot),
        ...(productId ? { product: productId } : {}),
      },
    });
    this.kbLinks = (data.data || []).map(this.kbLinkAdapter);
  };

  createKbLink = async (link: CatalogKbLinkInput) => {
    const bot = this.requireBot();
    const { data } = await axios.post(
      `${this.baseUrl(bot.id)}/kb-links`,
      this.kbLinkPayload(link),
      { params: this.authParams(bot) }
    );
    const created = this.kbLinkAdapter(data.data);
    this.kbLinks = [created, ...this.kbLinks.filter((item) => item.id !== created.id)];
    return created;
  };

  deleteKbLink = async (id: number) => {
    const bot = this.requireBot();
    await axios.delete(`${this.baseUrl(bot.id)}/kb-links/${id}`, {
      params: this.authParams(bot),
    });
    this.kbLinks = this.kbLinks.filter((link) => link.id !== id);
  };

  resolveLinks = async (documents: CatalogResolveDocument[]) => {
    const bot = this.requireBot();
    const { data } = await axios.post(
      `${this.baseUrl(bot.id)}/resolve`,
      { documents: documents.map(this.resolveDocumentPayload) },
      { params: this.authParams(bot) }
    );
    const proposals: CatalogKbLink[] = (data.data || []).map(this.kbLinkAdapter);
    this.kbLinks = [
      ...this.kbLinks.filter(
        (link) => !proposals.some((proposal) => proposal.id === link.id)
      ),
      ...proposals,
    ];
    return proposals;
  };

  private requireBot() {
    if (!bots.selectedBot) {
      throw new Error("A bot must be selected before loading Catalog V2.");
    }

    return bots.selectedBot;
  }

  private baseUrl(botId: number) {
    return `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${botId}/catalog`;
  }

  private authParams(bot: { key?: string; password?: string }) {
    return {
      key: bot.key,
      password: bot.password,
      userId: auth.user?.id,
    };
  }

  private categoryPayload(category: Partial<CatalogCategoryInput>) {
    return {
      parent_id: category.parentId,
      axis: category.axis,
      name: category.name,
      description: category.description,
      metadata: category.metadata,
      slug: category.slug,
      position: category.position,
    };
  }

  private productPayload(product: CatalogProductInput) {
    return {
      name: product.name,
      slug: product.slug,
      status: product.status,
      base_price: product.basePrice,
      currency: product.currency,
      variant: product.variant,
      description: product.description,
      attributes: product.attributes,
      media: product.media,
      docs: product.docs,
      external_ref: product.externalRef,
      categories: product.categories,
      primary_category_id: product.primaryCategoryId,
    };
  }

  private kbLinkPayload(link: CatalogKbLinkInput) {
    return {
      source_type: link.sourceType,
      source_id: link.sourceId,
      product_id: link.productId,
      category_id: link.categoryId,
      confidence: link.confidence,
    };
  }

  private resolveDocumentPayload(document: CatalogResolveDocument) {
    return {
      entity: document.entity,
      title: document.title,
      content: document.content,
      source_type: document.sourceType,
      source_id: document.sourceId,
    };
  }

  private categoryAdapter = (category: any): CatalogCategory => ({
    id: Number(category.id),
    botId: Number(category.bot_id),
    parentId: category.parent_id === null || category.parent_id === undefined ? null : Number(category.parent_id),
    axis: category.axis || "",
    name: category.name || "",
    description: category.description ?? null,
    metadata: category.metadata || {},
    slug: category.slug || "",
    position: Number(category.position || 0),
    children: (category.children || []).map(this.categoryAdapter),
  });

  private productAdapter = (product: any): CatalogProduct => {
    const aliasItems = (product.aliases || []).map(this.aliasAdapter);

    return {
      id: Number(product.id),
      botId: Number(product.bot_id),
      name: product.name || "",
      slug: product.slug || "",
      status: product.status || "active",
      basePrice:
        product.base_price === null || product.base_price === undefined
          ? null
          : Number(product.base_price),
      currency: product.currency ?? null,
      variant: product.variant ?? null,
      description: product.description ?? null,
      attributes: product.attributes || {},
      media: product.media || [],
      docs: product.docs || [],
      externalRef: product.external_ref ?? null,
      categories: (product.categories || []).map((category: any) => Number(category.id ?? category)),
      aliases: aliasItems.map((alias: CatalogProductAlias) => alias.alias),
      aliasItems,
      kbLinks: (product.kb_links || []).map(this.kbLinkAdapter),
    };
  };

  private aliasAdapter = (alias: any): CatalogProductAlias => ({
    id: Number(alias.id),
    botId:
      alias.bot_id === null || alias.bot_id === undefined
        ? undefined
        : Number(alias.bot_id),
    productId: Number(alias.product_id),
    alias: alias.alias || "",
    source: alias.source,
  });

  private kbLinkAdapter = (link: any): CatalogKbLink => ({
    id: Number(link.id),
    botId: Number(link.bot_id),
    productId:
      link.product_id === null || link.product_id === undefined
        ? null
        : Number(link.product_id),
    categoryId:
      link.category_id === null || link.category_id === undefined
        ? null
        : Number(link.category_id),
    sourceType: link.source_type,
    sourceId: link.source_id,
    confidence:
      link.confidence === null || link.confidence === undefined
        ? null
        : Number(link.confidence),
  });

  private activityLogAdapter = (log: any): CatalogActivityLog => ({
    id: Number(log.id),
    botId: Number(log.bot_id),
    entityType: log.entity_type,
    entityId:
      log.entity_id === null || log.entity_id === undefined
        ? null
        : Number(log.entity_id),
    entityName: log.entity_name ?? null,
    action: log.action,
    actorId: log.actor_id ?? null,
    content: log.content || "",
    meta: log.meta || {},
    createdAt: DateTime.fromISO(log.created_at, { zone: "utc" }).toLocal(),
    updatedAt: DateTime.fromISO(log.updated_at, { zone: "utc" }).toLocal(),
  });
}

export default new CatalogV2Store();
